import React, { useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import type { Callout } from "../../models/API";
import SourceBadge from "./SourceBadge";

interface CalloutCardProps {
  callout: Callout;
  cardNumber?: number;
  isStale: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (callout: Callout) => void;
  onDelete: (calloutId: number) => void;
}

/** Format a timestamp string for display */
function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return ts;
  }
}

/** Render an inline SVG sparkline from sparkline_data values */
function SparklineSvg({
  values,
  status,
  label,
  cardId,
}: {
  values: number[];
  status: string;
  label?: string | null;
  cardId: string;
}) {
  if (!values || values.length < 2) return null;

  const color = status === "positive" ? "#2E7D32" : "#D32F2F";
  const width = 80;
  const height = 22;
  const yMin = 2;
  const yMax = 20;

  // Map values to y-coordinates (inverted: higher value = lower y)
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const spread = maxV - minV || 1;

  const points = values.map(
    (v) => yMin + ((maxV - v) / spread) * (yMax - yMin)
  );
  const xs = values.map((_, i) =>
    Math.round((width * i) / (values.length - 1))
  );

  const polylinePoints = xs
    .map((x, i) => `${x},${points[i].toFixed(1)}`)
    .join(" ");
  const polygonPoints = `${polylinePoints} ${xs[xs.length - 1]},${yMax} ${
    xs[0]
  },${yMax}`;
  const gradId = `spark-grad-${cardId}`;

  const lastX = xs[xs.length - 1];
  const lastY = points[points.length - 1];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "1px",
        flexShrink: 0,
      }}
    >
      {label && (
        <Box
          component="span"
          sx={{
            fontSize: "9px",
            fontWeight: "bold",
            textTransform: "uppercase",
            letterSpacing: "0.3px",
            color: "#BDBDBD",
            lineHeight: 1,
          }}
        >
          {label}
        </Box>
      )}
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block" }}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.15} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <line
          x1="0"
          y1="11"
          x2={width}
          y2="11"
          stroke="#D0D0D0"
          strokeWidth="0.6"
          strokeDasharray="2,2"
        />
        <polygon points={polygonPoints} fill={`url(#${gradId})`} />
        <polyline
          points={polylinePoints}
          fill="none"
          stroke={color}
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <circle cx={lastX} cy={lastY.toFixed(1)} r="2.5" fill="#FFFFFF" />
        <circle cx={lastX} cy={lastY.toFixed(1)} r="1.5" fill={color} />
      </svg>
    </Box>
  );
}

const CalloutCard: React.FC<CalloutCardProps> = ({
  callout,
  cardNumber = 0,
  isStale,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
}) => {
  const [whyOpen, setWhyOpen] = useState(true);
  const isPositive = callout.status === "positive";
  const statusColor = isPositive ? "#2E7D32" : "#B85450";
  const statusBg = isPositive ? "#E8F5E9" : "#FFEBEE";
  const statusLabel = isPositive ? "▲ Positive" : "▼ Watch";

  // Show updated_by only if different from creator
  const showUpdatedBy =
    callout.updated_by_name &&
    callout.creator_name &&
    callout.updated_by_name !== callout.creator_name;

  return (
    <Box
      className="callout-card"
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: "10px",
        boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
        p: "12px 16px",
        mb: "10px",
        borderLeft: `4px solid ${statusColor}`,
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
        },
      }}
    >
      {/* Top row: number + badges + source chip + stale + edit/delete */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          mb: "8px",
          flexWrap: "wrap",
        }}
      >
        {/* Card number circle */}
        {callout.card_number > 0 && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              borderRadius: "50%",
              bgcolor: statusColor,
              color: "#FFFFFF",
              fontWeight: "bold",
              fontSize: 12,
              flexShrink: 0,
            }}
          >
            {callout.card_number}
          </Box>
        )}

        {/* Status badge — matches AI card .status-badge */}
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "10px",
            fontWeight: "bold",
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            padding: "3px 8px",
            borderRadius: "20px",
            bgcolor: statusBg,
            color: statusColor,
            flexShrink: 0,
          }}
        >
          {statusLabel}
        </Box>

        {/* Trend badge — matches AI card .trend-badge */}
        {callout.trend && (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              fontSize: "10px",
              fontWeight: "bold",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              padding: "3px 8px",
              borderRadius: "20px",
              bgcolor: "#EEF2FF",
              color: "#3B4DB8",
              flexShrink: 0,
            }}
          >
            {callout.trend}
          </Box>
        )}

        {/* Source badge — replaces hardcoded "Manual" chip; renders AI/Manual */}
        <SourceBadge source={callout.source} />

        {/* Impact badge */}
        {callout.impact && (
          <Box
            component="span"
            className={`impact-badge impact-${callout.impact}`}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              fontSize: "10px",
              fontWeight: "bold",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
              padding: "2px 6px",
              borderRadius: "4px",
              bgcolor: "#F0F0F0",
              color: "#424242",
              flexShrink: 0,
            }}
          >
            {callout.impact.charAt(0).toUpperCase() + callout.impact.slice(1)}
          </Box>
        )}

        {/* Sparkline — pushed right with edit/delete buttons */}
        {callout.sparkline_data &&
          callout.sparkline_data.values &&
          callout.sparkline_data.values.length >= 2 && (
            <Box sx={{ marginLeft: "auto", flexShrink: 0 }}>
              <SparklineSvg
                values={callout.sparkline_data.values}
                status={callout.status}
                label={callout.sparkline_data.label}
                cardId={`card-${callout.id}`}
              />
            </Box>
          )}

        {/* Stale indicator */}
        {isStale && (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              fontSize: "10px",
              fontWeight: "bold",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              padding: "3px 8px",
              borderRadius: "20px",
              bgcolor: "#fff3e0",
              color: "#e65100",
              flexShrink: 0,
            }}
          >
            Stale
          </Box>
        )}

        {/* Edit/Delete buttons — pushed right when no sparkline */}
        <Box
          sx={{
            marginLeft:
              (callout.sparkline_data?.values?.length ?? 0) >= 2
                ? undefined
                : "auto",
            display: "flex",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          {canEdit && (
            <Tooltip title="Edit callout">
              <IconButton size="small" onClick={() => onEdit(callout)}>
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete callout">
              <IconButton size="small" onClick={() => onDelete(callout.id)}>
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Headline — matches AI card .headline */}
      <Box
        sx={{
          fontSize: "13px",
          fontWeight: "bold",
          color: "#000000",
          lineHeight: 1.4,
          mb: "10px",
        }}
      >
        {callout.title}
      </Box>

      {/* Why Called Out — collapsible, matches AI card .why-details */}
      {callout.why_text && (
        <Box
          sx={{
            mt: "10px",
            borderTop: "1px solid #F0F0F0",
            pt: "6px",
          }}
        >
          <Box
            onClick={() => setWhyOpen(!whyOpen)}
            sx={{
              fontSize: "10px",
              fontWeight: "bold",
              textTransform: "uppercase",
              color: "#757575",
              letterSpacing: "0.5px",
              cursor: "pointer",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "3px 0",
              userSelect: "none",
              "&:hover": { color: "#424242" },
            }}
          >
            Why Called Out
            <Box
              component="span"
              sx={{
                fontSize: "10px",
                transition: "transform 0.2s ease",
                transform: whyOpen ? "rotate(180deg)" : "rotate(0deg)",
                display: "inline-block",
              }}
            >
              ▼
            </Box>
          </Box>
          {whyOpen && (
            <Typography
              component="div"
              sx={{
                fontSize: "12px",
                color: "#424242",
                lineHeight: 1.6,
                pt: "8px",
                whiteSpace: "pre-wrap",
                "& u": {
                  fontSize: "inherit",
                  fontWeight: "normal",
                  fontStyle: "normal",
                },
              }}
              dangerouslySetInnerHTML={{
                __html: (callout.why_text || "")
                  .replace(/&/g, "&amp;")
                  .replace(/</g, "&lt;")
                  .replace(/>/g, "&gt;")
                  .replace(
                    /(Anomaly|Root Cause|Recommendation|Impact|Action Items|Next Steps|Resolution|Mitigation|Background|Context|Evidence|Analysis):/g,
                    "<u>$1</u>:"
                  ),
              }}
            />
          )}
        </Box>
      )}

      {/* Footer: creator + updated_by (if different) + timestamp */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mt: 1.5,
          flexWrap: "wrap",
        }}
      >
        {callout.creator_name && (
          <Typography
            variant="caption"
            sx={{ color: "#9E9E9E", fontStyle: "italic" }}
          >
            Created by {callout.creator_name}
          </Typography>
        )}
        {showUpdatedBy && (
          <>
            <Typography variant="caption" sx={{ color: "#D0D0D0" }}>
              ·
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#9E9E9E", fontStyle: "italic" }}
            >
              Updated by {callout.updated_by_name}
            </Typography>
          </>
        )}
        <Typography
          variant="caption"
          sx={{ color: "#BDBDBD", marginLeft: "auto" }}
        >
          {formatTimestamp(callout.updated_at)}
        </Typography>
      </Box>
    </Box>
  );
};

export default CalloutCard;
